/**
 * <collections-header>
 * Tabbed style picker for PLP. Each tile links to a separate collection.
 * Clicking a tile swaps the product-grid section in place (no full reload)
 * using Shopify's section rendering API, then pushState's the new URL.
 * Falls back to normal navigation if anything is missing or fetch fails.
 */
class CollectionsHeader extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector("[data-collections-header-track]");
    this.prevArrow = this.querySelector("[data-collections-header-prev]");
    this.nextArrow = this.querySelector("[data-collections-header-next]");
    this.loadingRegion = this.querySelector(".collections-header__loading");

    this.panel = this.querySelector("[data-ch-panel]");
    this.toggle = this.querySelector("[data-ch-toggle]");
    this.toggleValue = this.querySelector("[data-ch-toggle-value]");
    this.collapseMode = this.dataset.collapseMode || "never";

    this.onTileClick = this.onTileClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.updateArrow = this.updateArrow.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);
    this.applyCollapseMode = this.applyCollapseMode.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.onTileClick);
    window.addEventListener("popstate", this.onPopState);

    if (this.nextArrow) {
      this.nextArrow.addEventListener("click", () => this.scrollByPage(1));
    }
    if (this.prevArrow) {
      this.prevArrow.addEventListener("click", () => this.scrollByPage(-1));
    }
    if (this.track) {
      this.track.addEventListener("scroll", this.updateArrow, { passive: true });
      window.addEventListener("resize", this.updateArrow);
      // Defer so layout is settled.
      requestAnimationFrame(this.updateArrow);
    }

    this.setupCollapse();
    this.setupDrag();
  }

  disconnectedCallback() {
    window.removeEventListener("popstate", this.onPopState);
    window.removeEventListener("resize", this.updateArrow);
    if (this.mobileQuery) {
      this.mobileQuery.removeEventListener("change", this.applyCollapseMode);
    }
  }

  /* ------------------------------------------------------------------- drag */

  /**
   * Drag to scroll the track with a mouse. Touch already scrolls natively, so
   * this is pointer-only — binding touch as well would fight the native
   * momentum. A drag past a few pixels suppresses the click that follows, or
   * releasing over a tile would navigate.
   */
  setupDrag() {
    if (!this.track) return;

    var down = false;
    var moved = 0;
    var startX = 0;
    var startScroll = 0;
    var track = this.track;

    track.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "touch" || event.button !== 0) return;
      down = true;
      moved = 0;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      track.classList.add("is-dragging");
    });

    track.addEventListener("pointermove", (event) => {
      if (!down) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > moved) moved = Math.abs(dx);
      if (moved > 3) {
        // Only capture once it is clearly a drag, so a plain click still works.
        if (track.hasPointerCapture && !track.hasPointerCapture(event.pointerId)) {
          try { track.setPointerCapture(event.pointerId); } catch (e) {}
        }
        event.preventDefault();
        track.scrollLeft = startScroll - dx;
      }
    });

    const end = (event) => {
      if (!down) return;
      down = false;
      track.classList.remove("is-dragging");
      if (event && event.pointerId && track.releasePointerCapture) {
        try { track.releasePointerCapture(event.pointerId); } catch (e) {}
      }
      if (moved > 3) {
        // Swallow the click this drag would otherwise produce.
        const swallow = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
        };
        track.addEventListener("click", swallow, { capture: true, once: true });
        // If no click follows, do not leave the handler armed.
        setTimeout(() => track.removeEventListener("click", swallow, true), 0);
      }
    };

    track.addEventListener("pointerup", end);
    track.addEventListener("pointercancel", end);
    track.addEventListener("pointerleave", end);
  }

  /* ---------------------------------------------------------------- collapse */

  get storageKey() {
    return `ch-collapsed-${this.dataset.sectionId || "default"}`;
  }

  setupCollapse() {
    if (this.collapseMode === "never" || !this.panel || !this.toggle) return;

    this.toggle.hidden = false;
    this.toggle.addEventListener("click", this.onToggleClick);

    if (this.collapseMode === "mobile") {
      this.mobileQuery = window.matchMedia("(max-width: 749px)");
      this.mobileQuery.addEventListener("change", this.applyCollapseMode);
    }

    this.applyCollapseMode();
  }

  /** Does collapsing apply at this viewport width? */
  get collapseApplies() {
    if (this.collapseMode === "always") return true;
    if (this.collapseMode === "mobile") return this.mobileQuery ? this.mobileQuery.matches : false;
    return false;
  }

  /**
   * Called on load and whenever the breakpoint is crossed. Above the mobile
   * breakpoint the toggle is removed entirely and the panel forced open, so a
   * state saved on a phone can never leave the desktop styles hidden.
   */
  applyCollapseMode() {
    if (!this.collapseApplies) {
      this.toggle.hidden = true;
      this.setCollapsed(false, false);
      return;
    }

    this.toggle.hidden = false;
    this.setCollapsed(this.readStoredState(), false);
    this.updateToggleValue();
  }

  readStoredState() {
    try {
      const stored = window.sessionStorage.getItem(this.storageKey);
      if (stored === "open") return false;
      if (stored === "closed") return true;
    } catch (e) {
      // Private browsing or blocked storage — fall through to the default.
    }
    return (this.dataset.collapseDefault || "closed") === "closed";
  }

  onToggleClick() {
    this.setCollapsed(!this.classList.contains("is-collapsed"), true);
  }

  setCollapsed(collapsed, persist) {
    this.classList.toggle("is-collapsed", collapsed);
    this.toggle.setAttribute("aria-expanded", collapsed ? "false" : "true");

    // Keep collapsed tiles out of the tab order.
    if ("inert" in HTMLElement.prototype) {
      this.panel.inert = collapsed;
    }

    if (persist) {
      try {
        window.sessionStorage.setItem(this.storageKey, collapsed ? "closed" : "open");
      } catch (e) {
        // Nothing to do — the toggle still works for this page view.
      }
    }

    if (!collapsed) {
      // Arrow visibility is measured from layout, which was zero while closed.
      requestAnimationFrame(this.updateArrow);
    }
  }

  /** Show the selected style next to the label so it reads while closed. */
  updateToggleValue() {
    if (!this.toggleValue) return;
    const active = this.querySelector(".collections-header__tile--active .collections-header__label");
    this.toggleValue.textContent = active ? active.textContent.trim() : "";
  }

  scrollByPage(direction) {
    if (!this.track) return;
    this.track.scrollBy({ left: this.track.clientWidth * 0.8 * direction, behavior: "smooth" });
  }

  /**
   * The arrows are only shown when the track overflows, and each is disabled at
   * its end of the travel — same behaviour as the product carousel.
   */
  updateArrow() {
    if (!this.track) return;

    const overflow = this.track.scrollWidth - this.track.clientWidth;
    const scrollable = overflow > 2;
    this.classList.toggle("is-scrollable", scrollable);

    if (this.prevArrow) {
      this.prevArrow.disabled = !scrollable || this.track.scrollLeft <= 2;
    }
    if (this.nextArrow) {
      this.nextArrow.disabled = !scrollable || this.track.scrollLeft >= overflow - 2;
    }
  }

  /**
   * Resolve the product-grid section's Shopify wrapper id from the DOM.
   * `data-id` on the grid equals the section id used by ?section_id=.
   */
  getGridSectionId() {
    const grid = document.querySelector("#ProductGridContainer [data-id]");
    return grid ? grid.getAttribute("data-id") : null;
  }

  onTileClick(event) {
    const tile = event.target.closest(".collections-header__tile");
    if (!tile) return;

    // Respect modifier keys / middle-click → let the browser handle it.
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const url = tile.getAttribute("data-collection-url");
    const handle = tile.getAttribute("data-collection-handle");
    const sectionId = this.getGridSectionId();

    // No grid on the page or already active → fall back / no-op.
    if (!url || !sectionId) return;
    if (tile.classList.contains("collections-header__tile--active")) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.navigateTo(url, handle, sectionId, true);
  }

  onPopState() {
    const sectionId = this.getGridSectionId();
    if (!sectionId) return;

    const path = window.location.pathname;
    const tile = this.querySelector(`[data-collection-url^="${path}"]`);
    const handle = tile ? tile.getAttribute("data-collection-handle") : null;
    // Only intercept navigations that target one of our tab collections.
    if (!handle) return;

    this.navigateTo(window.location.href, handle, sectionId, false);
  }

  setLoading(isLoading) {
    this.classList.toggle("is-loading", isLoading);
    if (this.loadingRegion) {
      this.loadingRegion.textContent = isLoading ? "Loading products…" : "";
    }
  }

  setActiveTile(handle) {
    this.querySelectorAll(".collections-header__tile").forEach((tile) => {
      const isActive = tile.getAttribute("data-collection-handle") === handle;
      tile.classList.toggle("collections-header__tile--active", isActive);
      tile.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) {
        tile.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
      }
    });
    this.setAttribute("data-current-collection", handle);
    this.updateToggleValue();
  }

  navigateTo(url, handle, sectionId, pushState) {
    const wrapper = document.getElementById(`shopify-section-${sectionId}`);
    if (!wrapper) {
      window.location.assign(url);
      return;
    }

    this.setLoading(true);
    // Optimistically mark the tab active for immediate feedback.
    this.setActiveTile(handle);

    const fetchUrl = `${url.split("?")[0]}?section_id=${encodeURIComponent(sectionId)}`;

    fetch(fetchUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then((html) => {
        wrapper.innerHTML = html;

        if (pushState) {
          window.history.pushState({ collectionsHeader: true }, "", url);
        }

        // Move keyboard/scroll focus to the refreshed grid for a11y, without jumping the page.
        const grid = document.getElementById("ProductGridContainer");
        if (grid) {
          grid.setAttribute("tabindex", "-1");
        }

        document.dispatchEvent(
          new CustomEvent("collections-header:loaded", {
            bubbles: true,
            detail: { handle, url, sectionId },
          })
        );
      })
      .catch(() => {
        // Hard fallback: navigate normally.
        window.location.assign(url);
      })
      .finally(() => {
        this.setLoading(false);
      });
  }
}

if (!customElements.get("collections-header")) {
  customElements.define("collections-header", CollectionsHeader);
}
